/**
 * Proveedor de autenticación con Microsoft Entra ID vía MSAL.
 *
 * Flujo: Authorization Code + PKCE con `loginRedirect` /
 * `handleRedirectPromise`. El access token para el API se obtiene con
 * `acquireTokenSilent({ scopes: [API_SCOPE] })`, que MSAL renueva de forma
 * transparente; si falla por requerir interacción, caemos a
 * `acquireTokenRedirect`.
 *
 * El backend valida ese access token (audiencia del API) y hace el upsert
 * del usuario a partir de sus claims. El frontend solo necesita name/email/
 * roles para la UI.
 *
 * Implementa el contrato común `AuthProvider` de `./types`.
 */

import {
  AuthenticationResult,
  type AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
  type RedirectRequest,
  type SilentRequest,
} from "@azure/msal-browser";
import { AuthError, type AuthProvider, type AuthUser } from "./types";

// --- Config desde entorno ----------------------------------------------------

const TENANT_ID = import.meta.env.VITE_ENTRA_TENANT_ID ?? "";
const CLIENT_ID = import.meta.env.VITE_ENTRA_CLIENT_ID ?? "";
const API_SCOPE = import.meta.env.VITE_ENTRA_API_SCOPE ?? "";
const REDIRECT_URI =
  import.meta.env.VITE_ENTRA_REDIRECT_URI || window.location.origin;
const AUTHORITY = `https://login.microsoftonline.com/${TENANT_ID}`;

// Scopes para el access token que viaja como Bearer al API.
const API_SCOPES = API_SCOPE ? [API_SCOPE] : [];
// Scopes para el login (identidad). openid+profile garantizan el id_token con
// los claims de nombre/email/roles; email añade el claim de correo.
const LOGIN_SCOPES = ["openid", "profile", "email", ...API_SCOPES];

// --- Instancia MSAL (perezosa) -----------------------------------------------
// IMPORTANTE: no instanciar a nivel de módulo. MSAL usa crypto.subtle, que solo
// existe en contextos seguros (HTTPS o localhost). En HTTP plano (p.ej. acceso
// on-prem por IP) el constructor lanza y tumbaría TODA la app aunque el
// proveedor activo sea Keycloak (este módulo se importa siempre). Por eso la
// instancia se crea de forma perezosa, solo cuando se usa realmente Entra.
let _msal: PublicClientApplication | null = null;
function msal(): PublicClientApplication {
  if (!_msal) {
    _msal = new PublicClientApplication({
      auth: {
        clientId: CLIENT_ID,
        authority: AUTHORITY,
        redirectUri: REDIRECT_URI,
        postLogoutRedirectUri: REDIRECT_URI,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        // sessionStorage: la sesión se borra al cerrar la pestaña, coherente con
        // el proveedor keycloak (evita herencia de credenciales en equipos
        // compartidos).
        cacheLocation: "sessionStorage",
        storeAuthStateInCookie: false,
      },
    });
  }
  return _msal;
}

// Cache en memoria del último access token válido, para que `getAccessToken()`
// (síncrono) pueda servir un Bearer sin tocar la red.
let cachedAccessToken: string | null = null;

let initialized = false;
let initPromise: Promise<void> | null = null;
let inFlightSilent: Promise<string | null> | null = null;

interface EntraIdTokenClaims {
  oid?: string;
  sub?: string;
  name?: string;
  preferred_username?: string;
  email?: string;
  roles?: string[];
}

function userFromAccount(account: AccountInfo): AuthUser {
  const claims = (account.idTokenClaims ?? {}) as EntraIdTokenClaims;
  return {
    id: claims.oid ?? account.localAccountId ?? account.homeAccountId,
    email: account.username ?? claims.email ?? claims.preferred_username ?? "",
    name: account.name ?? claims.name ?? account.username ?? "",
    roles: claims.roles ?? [],
  };
}

function activeAccount(): AccountInfo | null {
  const active = msal().getActiveAccount();
  if (active) return active;
  const all = msal().getAllAccounts();
  if (all.length > 0) {
    msal().setActiveAccount(all[0]);
    return all[0];
  }
  return null;
}

function applyResult(result: AuthenticationResult | null): void {
  if (!result) return;
  if (result.account) msal().setActiveAccount(result.account);
  if (result.accessToken) cachedAccessToken = result.accessToken;
}

/**
 * Inicialización idempotente: `initialize()` de MSAL + procesado de la
 * respuesta de redirect (si volvemos del login externo). Debe completarse
 * antes de cualquier otra llamada a MSAL.
 */
async function initialize(): Promise<void> {
  if (initialized) return;
  if (initPromise) return initPromise;
  initPromise = (async () => {
    await msal().initialize();
    const redirectResult = await msal().handleRedirectPromise();
    applyResult(redirectResult);
    // Asegura cuenta activa tras un F5 con sesión ya cacheada.
    activeAccount();
    initialized = true;
  })();
  return initPromise;
}

/** Login con credenciales: no aplica en Entra. */
async function login(): Promise<AuthUser> {
  throw new AuthError(
    0,
    "El proveedor Entra usa login interactivo de Microsoft, no por formulario."
  );
}

/** Login interactivo: redirige a la pantalla de Microsoft. */
async function loginInteractive(): Promise<void> {
  await initialize();
  const request: RedirectRequest = { scopes: LOGIN_SCOPES };
  await msal().loginRedirect(request);
  // loginRedirect navega fuera de la página; no se ejecuta nada más aquí.
}

async function logout(): Promise<void> {
  cachedAccessToken = null;
  const account = activeAccount() ?? undefined;
  await initialize();
  await msal().logoutRedirect({
    account,
    postLogoutRedirectUri: REDIRECT_URI,
  });
}

/**
 * Devuelve un access token vigente para el API. `acquireTokenSilent` renueva
 * de forma transparente; si requiere interacción, redirigimos al login.
 * Coalescing: una sola llamada silent en vuelo aunque varios requests la pidan.
 */
async function refreshIfNeeded(): Promise<string | null> {
  await initialize();
  const account = activeAccount();
  if (!account) return null;
  if (inFlightSilent) return inFlightSilent;

  inFlightSilent = (async () => {
    const request: SilentRequest = { scopes: API_SCOPES, account };
    try {
      const result = await msal().acquireTokenSilent(request);
      applyResult(result);
      return result.accessToken ?? null;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // No se puede renovar en silencio: hace falta interacción del usuario.
        await msal().acquireTokenRedirect({ scopes: API_SCOPES, account });
        return null;
      }
      return null;
    } finally {
      inFlightSilent = null;
    }
  })();

  return inFlightSilent;
}

function getAccessToken(): string | null {
  return cachedAccessToken;
}

function getCurrentUser(): AuthUser | null {
  const account = activeAccount();
  return account ? userFromAccount(account) : null;
}

/**
 * Al cargar la app: si hay una cuenta MSAL en cache, intenta obtener un
 * access token vigente y devuelve el usuario. Si no hay cuenta, devuelve null
 * (el guard del AuthProvider mandará al login).
 */
async function restoreSession(): Promise<AuthUser | null> {
  await initialize();
  const account = activeAccount();
  if (!account) return null;
  // Calienta el access token cacheado para los primeros requests; si requiere
  // interacción esto disparará el redirect, lo cual es el comportamiento
  // deseado.
  await refreshIfNeeded();
  return userFromAccount(account);
}

export const entraProvider: AuthProvider = {
  kind: "entra",
  usesCredentialsForm: false,
  initialize,
  login,
  loginInteractive,
  logout,
  refreshIfNeeded,
  getAccessToken,
  getCurrentUser,
  restoreSession,
};
